/* eslint-disable no-restricted-syntax */
// Deprecated: kept for backward compatibility

/**
 * Theme breakpoint scale — single source of truth for JavaScript viewport checks.
 *
 * These values mirror the CSS utility class breakpoints in grid.css,
 * utilities.css, and spacing.css. CSS @media queries cannot reference these
 * values directly — if you change a breakpoint here, update those files too.
 *
 *   sm  (576px)  → .col-sm-*   .d-sm-*   .m-sm-*
 *   md  (768px)  → .col-md-*   .d-md-*   .m-md-*
 *   lg  (992px)  → .col-lg-*   .d-lg-*   .m-lg-*  ← mobile/desktop split
 *   xl  (1200px) → .col-xl-*   .d-xl-*   .m-xl-*
 *   xxl (1400px) → .col-xxl-*  .d-xxl-*  .m-xxl-*
 */
const BREAKPOINTS = {
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1200,
  xxl: 1400,
  ipadProMin: 1024,
  ipadProMax: 1366,
};

const TRAP_FOCUS_HANDLERS = {};
const Utility = {
  _swatchesCache: null,
  _addToCartInFlightCount: 0,

  /**
   * Marks an add-to-cart request as started. Call synchronously, before any
   * `await`, at the very start of the submit handler — localization-form.js
   * checks this to avoid firing its automatic US/CA market-switch reload
   * (which Shopify empties the cart as a side effect of) while an add-to-cart
   * is in flight, since that reload racing an in-flight `/cart/add.js` call
   * would otherwise discard the item being added.
   */
  markAddToCartStart() {
    Utility._addToCartInFlightCount = (Utility._addToCartInFlightCount || 0) + 1;
  },

  /** Pairs with markAddToCartStart() — call in a `finally` block. */
  markAddToCartEnd() {
    Utility._addToCartInFlightCount = Math.max(0, (Utility._addToCartInFlightCount || 0) - 1);
  },

  /** @returns {boolean} True while any markAddToCartStart() call hasn't been matched by markAddToCartEnd() yet. */
  isAddToCartInFlight() {
    return (Utility._addToCartInFlightCount || 0) > 0;
  },

  /**
   * Active Watkins market from localStorage (`watkins_country`). Defaults to US.
   * @returns {'US' | 'CA'}
   */
  getWatkinsCountry() {
    try {
      const iso = String(globalThis.localStorage?.getItem('watkins_country') || '').trim().toUpperCase();
      return iso === 'CA' ? 'CA' : 'US';
    } catch {
      return 'US';
    }
  },

  /**
   * Keeps only United States + Canada options on a Shopify country <select>
   * (preserves each option’s `data-provinces` for CountryProvinceSelector).
   * @param {HTMLSelectElement} countryEl
   */
  filterCountrySelectToUsCa(countryEl) {
    if (!countryEl || !countryEl.options) return;
    const keep = [];
    for (let i = 0; i < countryEl.options.length; i++) {
      const opt = countryEl.options[i];
      const value = String(opt.value || '').trim();
      const label = String(opt.textContent || '')
        .trim()
        .toLowerCase()
        .replace(/\u00a0/g, ' ');
      const isUs =
        value === 'United States' ||
        value === 'US' ||
        value === 'USA' ||
        label === 'united states' ||
        label === 'united states of america';
      const isCa = value === 'Canada' || value === 'CA' || value === 'CAN' || label === 'canada';
      if (isUs || isCa) keep.push(opt);
    }
    if (!keep.length) return;
    while (countryEl.firstChild) countryEl.removeChild(countryEl.firstChild);
    keep.forEach((opt) => countryEl.appendChild(opt));
  },

  /**
   * Selects US or CA on a country <select> from watkins_country / data-default.
   * @param {HTMLSelectElement} countryEl
   */
  selectUsOrCaCountry(countryEl) {
    if (!countryEl || !countryEl.options?.length) return;
    const tryVals = [];
    let wc = '';
    try {
      wc = String(globalThis.localStorage?.getItem('watkins_country') || '')
        .trim()
        .toUpperCase();
    } catch {
      wc = '';
    }
    if (wc === 'US' || wc === 'USA') tryVals.push('United States', 'US', 'USA');
    else if (wc === 'CA' || wc === 'CAN') tryVals.push('Canada', 'CA', 'CAN');

    if (!tryVals.length) {
      const def = String(countryEl.getAttribute('data-default') || '').trim();
      const defUp = def.toUpperCase();
      if (defUp === 'US' || defUp === 'USA') tryVals.push('United States', 'US', 'USA');
      else if (defUp === 'CA' || defUp === 'CAN') tryVals.push('Canada', 'CA', 'CAN');
      else if (def) tryVals.push(def);
    }
    if (!tryVals.length) {
      tryVals.push(Utility.getWatkinsCountry() === 'CA' ? 'Canada' : 'United States', 'US', 'CA');
    }

    let idx = -1;
    for (let v = 0; v < tryVals.length && idx < 0; v++) {
      const wanted = tryVals[v];
      for (let i = 0; i < countryEl.options.length; i++) {
        if (countryEl.options[i].value === wanted) {
          idx = i;
          break;
        }
      }
    }
    for (let v = 0; v < tryVals.length && idx < 0; v++) {
      const wanted = String(tryVals[v]).toLowerCase();
      for (let i = 0; i < countryEl.options.length; i++) {
        if ((countryEl.options[i].textContent || '').trim().toLowerCase() === wanted) {
          idx = i;
          break;
        }
      }
    }
    if (idx < 0 && typeof Shopify !== 'undefined' && typeof Shopify.setSelectorByValue === 'function') {
      Shopify.setSelectorByValue(countryEl, countryEl.getAttribute('data-default') || tryVals[0] || '');
      idx = countryEl.selectedIndex;
    }
    if (idx >= 0) {
      countryEl.selectedIndex = idx;
      countryEl.value = countryEl.options[idx].value;
      countryEl.dataset.default = countryEl.value;
    }
  },

  /**
   * US/CA country select + province/state dropdown (same pattern as consultant-province).
   * Requires `Shopify.CountryProvinceSelector` (template-account-address.js).
   * @param {string} countryId
   * @param {string} provinceId
   * @param {string} [hideElementId]
   * @returns {boolean}
   */
  initUsCaCountryProvinceSelector(countryId, provinceId, hideElementId) {
    if (typeof Shopify === 'undefined' || !Shopify.CountryProvinceSelector) return false;
    const countryEl = document.getElementById(countryId);
    const provinceEl = document.getElementById(provinceId);
    if (!countryEl || !provinceEl) return false;
    if (countryEl.dataset.usCaProvinceInit === 'true') {
      Utility.selectUsOrCaCountry(countryEl);
      countryEl.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    }

    Utility.filterCountrySelectToUsCa(countryEl);
    Utility.selectUsOrCaCountry(countryEl);
    provinceEl.dataset.default = provinceEl.dataset.default || '';
    // eslint-disable-next-line no-new
    new Shopify.CountryProvinceSelector(countryId, provinceId, {
      hideElement: hideElementId || provinceId,
    });
    countryEl.dataset.usCaProvinceInit = 'true';
    return true;
  },

  /**
   * Preferred province label for consultant search APIs (full name when available).
   * @param {HTMLSelectElement | null | undefined} provinceEl
   * @returns {string}
   */
  getSelectedProvinceSearchValue(provinceEl) {
    if (!provinceEl || provinceEl.selectedIndex < 0) return '';
    const opt = provinceEl.options[provinceEl.selectedIndex];
    if (!opt || !opt.value) return '';
    const label = String(opt.textContent || '').trim();
    return label || String(opt.value).trim();
  },

  /**
   * Reads customer_category (metafield or tag) off the same globals theme.liquid exposes
   * (__watkinsCustomerCategory / __watkinsCustomerCategoryText / __watkinsCustomerTags).
   * Shared by getConsultantLinePropertyPrefix() and hasConsultantContextForLineProperties()
   * so both agree on who counts as HBB Associates/Customers.
   * @returns {{ isAssociates: boolean, isCustomers: boolean }}
   */
  getHbbCategoryFlags() {
    const labels = [];
    const pushLabel = (value) => {
      if (value == null) return;
      const normalize = (item) => {
        if (item == null) return '';
        if (typeof item === 'object' && !Array.isArray(item)) {
          const label = item.system?.title || item.title || item.name || '';
          return String(label).trim().toLowerCase().replace(/\u00a0/g, ' ');
        }
        return String(item).trim().toLowerCase().replace(/\u00a0/g, ' ');
      };
      if (Array.isArray(value)) {
        value.forEach((item) => {
          const label = normalize(item);
          if (label) labels.push(label);
        });
        return;
      }
      const label = normalize(value);
      if (label) labels.push(label);
    };

    pushLabel(globalThis.__watkinsCustomerCategory);
    pushLabel(globalThis.__watkinsCustomerCategoryText);
    pushLabel(globalThis.__watkinsCustomerTags);

    return {
      isAssociates: labels.some((label) => label === 'hbb associates'),
      isCustomers: labels.some((label) => label === 'hbb customers' || label === 'hbb customer'),
    };
  },

  /**
   * HBB Customers → `_` (hidden line props); HBB Associates / default → ``.
   * Guests with a consultant ID in localStorage are treated as HBB Customers.
   * @returns {'' | '_'}
   */
  getConsultantLinePropertyPrefix() {
    const { isAssociates, isCustomers } = Utility.getHbbCategoryFlags();

    if (isCustomers && !isAssociates) return '_';
    if (isAssociates) return '';

    // Guest shopping with a consultant → hidden props (HBB Customers style).
    if (
      globalThis.__watkinsCustomerLoggedIn !== true &&
      Utility.getWatkinsConsultantIdFromLocalStorage()
    ) {
      return '_';
    }

    return '';
  },

  /**
   * @returns {{ points: string, bonusUs: string, bonusCa: string, bonus: string }}
   */
  getConsultantLinePropertyKeys() {
    const prefix = Utility.getConsultantLinePropertyPrefix();
    return {
      points: `${prefix}Points`,
      bonusUs: `${prefix}Bonus US`,
      bonusCa: `${prefix}Bonus CA`,
      bonus: `${prefix}Bonus`,
    };
  },

  /** @returns {string} e.g. `properties[Points]` or `properties[_Points]` */
  getConsultantPointsPropertyName() {
    return `properties[${Utility.getConsultantLinePropertyKeys().points}]`;
  },

  /** @returns {string} e.g. `properties[Bonus US]` or `properties[_Bonus CA]` */
  getConsultantBonusPropertyName() {
    const keys = Utility.getConsultantLinePropertyKeys();
    const bonusKey = Utility.getWatkinsCountry() === 'CA' ? keys.bonusCa : keys.bonusUs;
    return `properties[${bonusKey}]`;
  },

  /** All Points/Bonus FormData keys used across categories (for delete/cleanup). */
  getConsultantLinePropertyFormNames() {
    return [
      'properties[Points]',
      'properties[_Points]',
      'properties[Bonus]',
      'properties[_Bonus]',
      'properties[Bonus US]',
      'properties[_Bonus US]',
      'properties[Bonus CA]',
      'properties[_Bonus CA]',
    ];
  },

  /**
   * Reads `watkins_consultantID` from localStorage (and theme helper JSON fallback).
   * @returns {string}
   */
  getWatkinsConsultantIdFromLocalStorage() {
    try {
      if (
        typeof WatkinsConsultantCheck !== 'undefined' &&
        typeof WatkinsConsultantCheck.getConsultantIdFromLocalStorage === 'function'
      ) {
        const fromHelper = String(WatkinsConsultantCheck.getConsultantIdFromLocalStorage() || '')
          .trim()
          .replace(/^#+/, '');
        if (fromHelper && fromHelper.length <= 64 && /^[\w.-]+$/i.test(fromHelper)) {
          return fromHelper;
        }
      }

      const raw = String(globalThis.localStorage?.getItem('watkins_consultantID') || '')
        .trim()
        .replace(/^#+/, '');
      if (raw && raw.length <= 64 && /^[\w.-]+$/i.test(raw)) return raw;
      return '';
    } catch {
      return '';
    }
  },

  /**
   * Guests must register/sign in before add-to-cart.
   * Shows a modal-dialog; Okay redirects to `/pages/register` with `return_to`.
   * @returns {boolean} true when login is required (caller should abort ATC)
   */
  requireCustomerLoginForCart() {
    if (globalThis.__watkinsCustomerLoggedIn === true) return false;

    const modal = document.getElementById('PopupModal-cart-login');
    if (modal && typeof modal.show === 'function') {
      Utility.bindCartLoginModalOnce(modal);
      modal.show();
      return true;
    }

    const returnTo = `${window.location.pathname}${window.location.search || ''}`;
    window.location.href = `/pages/register?return_to=${encodeURIComponent(returnTo)}`;
    return true;
  },

  /**
   * Wire Okay once: navigate to Join Watkins (`/pages/register`).
   * @param {HTMLElement} modal
   */
  bindCartLoginModalOnce(modal) {
    if (!modal || modal.dataset.cartLoginBound === '1') return;
    modal.dataset.cartLoginBound = '1';

    const okBtn = modal.querySelector('[data-cart-login-ok]');
    if (!okBtn) return;

    okBtn.addEventListener('click', () => {
      const returnTo = `${window.location.pathname}${window.location.search || ''}`;
      window.location.href = `/pages/register?return_to=${encodeURIComponent(returnTo)}`;
    });
  },

  /**
   * Site-wide "exclusive product" cart rule (settings.exclusive_cart_product):
   * while that product is in the cart, no other product may be added, and it
   * cannot be added itself while other products are already in the cart.
   * Shows a modal-dialog with settings.exclusive_cart_restriction_message.
   * @param {string|number|null|undefined} productId - product id being added to cart
   * @returns {boolean} true when the add-to-cart should be aborted
   */
  requireExclusiveCartProductCheck(productId) {
    const exclusiveId = globalThis.__watkinsExclusiveCartProductId;
    if (!exclusiveId || productId == null || productId === '') return false;

    const cartItems = globalThis.themeData?.cart?.items || [];
    const exclusiveInCart = cartItems.some((item) => String(item.product_id) === String(exclusiveId));
    const otherInCart = cartItems.some((item) => String(item.product_id) !== String(exclusiveId));
    const addingExclusive = String(productId) === String(exclusiveId);

    const blocked = (exclusiveInCart && !addingExclusive) || (otherInCart && addingExclusive);
    if (!blocked) return false;

    const modal = document.getElementById('PopupModal-exclusive-cart-restriction');
    if (modal && typeof modal.show === 'function') {
      Utility.bindExclusiveCartRestrictionModalOnce(modal);
      modal.show();
    }
    return true;
  },

  /**
   * Wire Okay once: send the shopper straight to checkout with the cart as-is
   * (the membership/exclusive product already in it).
   * @param {HTMLElement} modal
   */
  bindExclusiveCartRestrictionModalOnce(modal) {
    if (!modal || modal.dataset.exclusiveCartRestrictionBound === '1') return;
    modal.dataset.exclusiveCartRestrictionBound = '1';

    const okBtn = modal.querySelector('[data-exclusive-cart-restriction-ok]');
    if (!okBtn) return;

    okBtn.addEventListener('click', () => {
      window.location.href = Utility.getCheckoutUrl();
    });
  },

  /**
   * Storefront checkout URL, respecting a locale/market subfolder root when present.
   * @returns {string}
   */
  getCheckoutUrl() {
    if (typeof Shopify !== 'undefined' && Shopify.routes && Shopify.routes.root) {
      const root = String(Shopify.routes.root);
      if (root === '/' || root === '') return '/checkout';
      return (root.endsWith('/') ? root : `${root}/`) + 'checkout';
    }
    return '/checkout';
  },

  /**
   * Whether Points/Bonus line properties may be attached to cart lines.
   * True when the shopper is a consultant (`consultant` tag), HBB Associates / HBB Customers
   * (customer_category metafield or tag — a real Associate is not always also tagged
   * `consultant`), or `watkins_consultantID` is set in localStorage. Kept in sync with
   * getConsultantLinePropertyPrefix()/getHbbCategoryFlags() and the equivalent eligibility
   * checks in component-cart-form.js and watkins-consultant-points-bonus-pending.liquid —
   * this used to only recognize the `consultant` tag, so an Associate identified purely by
   * customer_category got no Points/Bonus attached at add-to-cart time at all.
   * @returns {boolean}
   */
  hasConsultantContextForLineProperties() {
    if (globalThis.__watkinsCustomerHasConsultantTag === true) return true;

    try {
      if (
        typeof WatkinsConsultantCheck !== 'undefined' &&
        typeof WatkinsConsultantCheck.isCustomerConsultantTag === 'function' &&
        WatkinsConsultantCheck.isCustomerConsultantTag()
      ) {
        return true;
      }
    } catch {
      /* ignore */
    }

    const { isAssociates, isCustomers } = Utility.getHbbCategoryFlags();
    if (isAssociates || isCustomers) return true;

    return Boolean(Utility.getWatkinsConsultantIdFromLocalStorage());
  },

  /**
   * Builds cart line `properties` matching PDP ATC.
   * HBB Associates: `Points`, `Bonus US` / `Bonus CA`.
   * HBB Customers: `_Points`, `_Bonus US` / `_Bonus CA`.
   * @param {Element | {
   *   points?: string,
   *   bonusUs?: string,
   *   bonusCa?: string,
   *   bonus?: string
   * } | null | undefined} source
   * @returns {Record<string, string> | null}
   */
  buildConsultantCartLineProperties(source) {
    if (!Utility.hasConsultantContextForLineProperties() || source == null) return null;

    let points = '';
    let bonusUs = '';
    let bonusCa = '';
    let bonusFallback = '';

    if (source instanceof Element) {
      const pointsHolder = source.matches?.('[data-variant-consultant-points]')
        ? source
        : source.querySelector?.('[data-variant-consultant-points]');
      const bonusHolder = source.matches?.('[data-variant-consultant-bonus]')
        ? source
        : source.querySelector?.('[data-variant-consultant-bonus]');

      points = String(
        source.getAttribute('data-consultant-points') ||
          pointsHolder?.dataset?.points ||
          pointsHolder?.querySelector?.('input[name="properties[Points]"]')?.value ||
          pointsHolder?.querySelector?.('input[name="properties[_Points]"]')?.value ||
          pointsHolder?.querySelector?.('input[type="hidden"]')?.value ||
          ''
      ).trim();

      if (bonusHolder) {
        bonusUs = String(bonusHolder.dataset.bonusUs || bonusHolder.dataset.bonus || '').trim();
        bonusCa = String(bonusHolder.dataset.bonusCa || '').trim();
        bonusFallback = String(
          bonusHolder.querySelector?.('[data-consultant-bonus-input]')?.value ||
            bonusHolder.dataset.bonus ||
            ''
        ).trim();
      } else {
        bonusUs = String(source.getAttribute('data-consultant-bonus-us') || '').trim();
        bonusCa = String(source.getAttribute('data-consultant-bonus-ca') || '').trim();
        bonusFallback = String(source.getAttribute('data-consultant-bonus') || '').trim();
      }
    } else if (typeof source === 'object') {
      points = String(source.points || '').trim();
      bonusUs = String(source.bonusUs || source.bonus || '').trim();
      bonusCa = String(source.bonusCa || '').trim();
      bonusFallback = String(source.bonus || '').trim();
    }

    const bonus = String(
      Utility.resolveConsultantBonus({
        bonusUs: bonusUs || bonusFallback,
        bonusCa,
        bonus: bonusFallback || bonusUs,
      }) || ''
    ).trim();

    const keys = Utility.getConsultantLinePropertyKeys();
    /** @type {Record<string, string>} */
    const properties = {};
    if (points) properties[keys.points] = points;
    if (bonus) {
      const bonusKey = Utility.getWatkinsCountry() === 'CA' ? keys.bonusCa : keys.bonusUs;
      properties[bonusKey] = bonus;
    }

    return Object.keys(properties).length ? properties : null;
  },

  /**
   * Resolves Bonus line property for the active market.
   * @param {{ bonusUs?: string, bonusCa?: string, bonus?: string } | Element | null} source
   * @returns {string}
   */
  resolveConsultantBonus(source) {
    let bonusUs = '';
    let bonusCa = '';
    let bonusFallback = '';

    if (source instanceof Element) {
      bonusUs = String(source.dataset.bonusUs || source.dataset.bonus || '').trim();
      bonusCa = String(source.dataset.bonusCa || '').trim();
    } else if (source && typeof source === 'object') {
      bonusUs = String(source.bonusUs || source.bonus || '').trim();
      bonusCa = String(source.bonusCa || '').trim();
      bonusFallback = String(source.bonus || '').trim();
    }

    if (!bonusUs && bonusFallback) bonusUs = bonusFallback;
    return Utility.getWatkinsCountry() === 'CA' ? (bonusCa || bonusUs) : bonusUs;
  },

  /**
   * Applies market-specific Bonus to all consultant bonus holders in scope.
   * @param {ParentNode} [container]
   */
  applyConsultantBonusForCountry(container = document) {
    const scope = container?.querySelectorAll ? container : document;
    scope.querySelectorAll('[data-variant-consultant-bonus]').forEach((holder) => {
      const bonus = Utility.resolveConsultantBonus(holder);
      const propName = Utility.getConsultantBonusPropertyName();
      let input = holder.querySelector('[data-consultant-bonus-input]');

      const bonusFormNames = new Set(
        Utility.getConsultantLinePropertyFormNames().filter((n) => n.includes('Bonus'))
      );
      holder.querySelectorAll('input[type="hidden"]').forEach((el) => {
        const name = el.getAttribute('name') || '';
        if (bonusFormNames.has(name)) {
          if (el !== input) el.remove();
        }
      });

      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.setAttribute('data-consultant-bonus-input', '');
        holder.appendChild(input);
      }

      input.setAttribute('name', propName);
      input.value = bonus;
      if (bonus) {
        holder.dataset.bonus = bonus;
      } else {
        delete holder.dataset.bonus;
      }
    });
  },

  /**
   * DOM Selection Utilities
   * Simplified methods for querying DOM elements
   */
  DomSelector: {
    /**
     * Query single element with optional parent context
     * @param {string} selector - CSS selector
     * @param {Element} context - Parent element (default: document)
     * @returns {Element|null}
     */
    $(selector, context = document) {
      return (context || document).querySelector(selector);
    },

    /**
     * Query multiple elements with optional parent context
     * @param {string} selector - CSS selector
     * @param {Element} context - Parent element (default: document)
     * @returns {Array}
     */
    $$(selector, context = document) {
      return Array.from((context || document).querySelectorAll(selector));
    },

    /**
     * Query element by ID
     * @param {string} id - Element ID (without #)
     * @returns {Element|null}
     */
    byId(id) {
      return document.getElementById(id);
    },

    /**
     * Check if element exists
     * @param {string} selector
     * @param {Element} context
     * @returns {boolean}
     */
    exists(selector, context = document) {
      return (context || document).querySelector(selector) !== null;
    }
  },

  /**
   * CSS Class Manipulation Utilities
   * Simplified methods for managing CSS classes on single or multiple elements
   */
  CSS: {
    /**
     * Add class to single or multiple elements
     * @param {Element|Element[]} elements - Single element or array of elements
     * @param {...string} classes - Classes to add
     * @example
     * Utility.CSS.add(element, 'open');
     * Utility.CSS.add([el1, el2], 'active', 'visible');
     */
    add(elements, ...classes) {
      if (!elements || classes.length === 0) return;
      const items = Array.isArray(elements) ? elements : [elements];
      items.forEach(el => el?.classList.add(...classes));
    },

    /**
     * Remove class from single or multiple elements
     * @param {Element|Element[]} elements - Single element or array of elements
     * @param {...string} classes - Classes to remove
     * @example
     * Utility.CSS.remove(element, 'open');
     * Utility.CSS.remove([el1, el2], 'active', 'visible');
     */
    remove(elements, ...classes) {
      if (!elements || classes.length === 0) return;
      const items = Array.isArray(elements) ? elements : [elements];
      items.forEach(el => el?.classList.remove(...classes));
    },

    /**
     * Toggle class on single or multiple elements
     * @param {Element|Element[]} elements - Single element or array of elements
     * @param {string} className - Class to toggle
     * @param {boolean} force - Optional: force add (true) or remove (false)
     * @example
     * Utility.CSS.toggle(element, 'open');
     * Utility.CSS.toggle(element, 'visible', true);
     */
    toggle(elements, className, force) {
      if (!elements || !className) return;
      const items = Array.isArray(elements) ? elements : [elements];
      items.forEach(el => el?.classList.toggle(className, force));
    },

    /**
     * Check if element has class
     * @param {Element} element - Element to check
     * @param {string} className - Class name
     * @returns {boolean}
     * @example
     * if (Utility.CSS.has(element, 'open')) { ... }
     */
    has(element, className) {
      return element?.classList.contains(className) || false;
    },

    /**
     * Replace one class with another
     * @param {Element|Element[]} elements - Single element or array of elements
     * @param {string} oldClass - Class to replace
     * @param {string} newClass - New class
     * @example
     * Utility.CSS.replace(element, 'loading', 'loaded');
     */
    replace(elements, oldClass, newClass) {
      if (!elements || !oldClass || !newClass) return;
      const items = Array.isArray(elements) ? elements : [elements];
      items.forEach(el => el?.classList.replace(oldClass, newClass));
    }
  },

  /**
   * IntersectionObserver Utilities
   * Simplified methods for observing element visibility
   */
  Observer: {
    /**
     * Create an IntersectionObserver with common defaults
     * @param {Function} callback - Function to call when intersection changes
     * @param {Object} options - IntersectionObserver options
     * @returns {IntersectionObserver}
     */
    create(callback, options = {}) {
      const defaultOptions = {
        root: null,
        rootMargin: '0px',
        threshold: 0,
        ...options
      };
      return new IntersectionObserver(callback, defaultOptions);
    },

    /**
     * Observe element once and disconnect after first intersection
     * @param {Element|string} element - Element or selector
     * @param {Function} onVisible - Callback when element becomes visible
     * @param {Object} options - Observer options
     * @returns {IntersectionObserver}
     */
    once(element, onVisible, options = {}) {
      const callback = (entries, observer) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            onVisible(entry.target, entry);
            observer.disconnect();
          }
        });
      };

      const observer = this.create(callback, options);
      const target = typeof element === 'string' ? Utility.DomSelector.$(element) : element;
      if (target) observer.observe(target);
      
      return observer;
    },

    /**
     * Observe element visibility changes (both enter and exit)
     * @param {Element|string} element - Element or selector
     * @param {Function} onEnter - Callback when element enters viewport
     * @param {Function} onExit - Callback when element exits viewport
     * @param {Object} options - Observer options
     * @returns {IntersectionObserver}
     */
    onChange(element, onEnter, onExit, options = {}) {
      const callback = (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && onEnter) {
            onEnter(entry.target, entry);
          } else if (!entry.isIntersecting && onExit) {
            onExit(entry.target, entry);
          }
        });
      };

      const observer = this.create(callback, options);
      const target = typeof element === 'string' ? Utility.DomSelector.$(element) : element;
      if (target) observer.observe(target);
      
      return observer;
    }
  },

  /**
   * URL & Query Parameter Utilities
   * Simplified methods for working with URLs and query parameters
   */
  URL: {
    /**
     * Get single query parameter value
     * @param {string} name - Parameter name
     * @param {string} url - URL to parse (default: current URL)
     * @returns {string|null}
     */
    getParam(name, url = window.location.search) {
      const params = new URLSearchParams(url);
      return params.get(name);
    },

    /**
     * Build query string from object or FormData
     * @param {Object|FormData} data - Object or FormData with key-value pairs
     * @returns {string} - Query string
     */
    buildQuery(data) {
      if (data instanceof FormData) {
        return new URLSearchParams(Array.from(data)).toString();
      }
      
      const params = new URLSearchParams();
      Object.entries(data).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          params.append(key, value);
        }
      });
      return params.toString();
    },

    /**
     * Update URL with query string (adds to browser history)
     * @param {string} queryString - Query string to set
     * @param {Object} options - Options {state, path}
     */
    updateUrl(queryString, options = {}) {
      const { state = {}, path = window.location.pathname } = options;
      const newUrl = queryString ? `${path}?${queryString}` : path;
      window.history.pushState(state, '', newUrl);
    },

    /**
     * Replace current URL without adding to browser history
     * @param {string} queryString - Query string to set
     * @param {Object} options - Options {state, path}
     */
    replaceUrl(queryString, options = {}) {
      const { state = {}, path = window.location.pathname } = options;
      const newUrl = queryString ? `${path}?${queryString}` : path;
      window.history.replaceState(state, '', newUrl);
    },

    /**
     * Remove hash from URL without adding to browser history
     * @param {Object} options - Options {state, keepQuery}
     */
    removeHash(options = {}) {
      const { state = {}, keepQuery = true } = options;
      const path = window.location.pathname;
      const search = keepQuery ? window.location.search : '';
      const newUrl = `${path}${search}`;
      window.history.replaceState(state, '', newUrl);
    }
  },

  /**
   * Animation & Transition Utilities
   * Simplified methods for common animation patterns
   */
  Animate: {
    /**
     * Internal fade effect implementation
     * @private
     * @param {Element} elem - Element to animate
     * @param {string} action - 'fadeIn' or 'fadeOut'
     */
    _fadeEffect(elem, action) {
      const FADE_STEP = 0.01;

      if (action === 'fadeIn') {
        elem.style.display = 'block';
        elem.style.opacity = 0;
        const fadeIn = () => {
          let newValue = parseFloat(elem.style.opacity);
          if (newValue < 1) {
            newValue += FADE_STEP;
            elem.style.opacity = newValue;
            requestAnimationFrame(fadeIn);
          } else {
            elem.style.opacity = 1;
          }
        };
        requestAnimationFrame(fadeIn);
      } else if (action === 'fadeOut') {
        elem.style.opacity = 1;
        const fadeOut = () => {
          let newValue = parseFloat(elem.style.opacity);
          if (newValue > 0) {
            newValue -= FADE_STEP;
            elem.style.opacity = newValue;
            requestAnimationFrame(fadeOut);
          } else {
            elem.style.opacity = 0;
            elem.style.display = 'none';
          }
        };
        requestAnimationFrame(fadeOut);
      }
    },

    /**
     * Fade in element
     * @param {Element|string} element - Element or selector
     * @param {Function} callback - Callback when fade completes (optional)
     */
    fadeIn(element, callback) {
      const target = typeof element === 'string' ? Utility.DomSelector.$(element) : element;
      if (!target) return;
      
      this._fadeEffect(target, 'fadeIn');
      if (callback) {
        callback();
      }
    },

    /**
     * Fade out element
     * @param {Element|string} element - Element or selector
     * @param {Function} callback - Callback when fade completes (optional)
     */
    fadeOut(element, callback) {
      const target = typeof element === 'string' ? Utility.DomSelector.$(element) : element;
      if (!target) return;
      
      this._fadeEffect(target, 'fadeOut');
      if (callback) {
        callback();
      }
    },

    /**
     * Show message with fade in/out effect
     * @param {Element|string} element - Element or selector
     * @param {number} duration - How long to show message in ms (default: 3000)
     */
    showMessage(element, duration = 3000) {
      const target = typeof element === 'string' ? Utility.DomSelector.$(element) : element;
      if (!target) return;
      
      this.fadeIn(target, () => {
        setTimeout(() => {
          this.fadeOut(target);
        }, duration);
      });
    },

    /**
     * Smooth class change using requestAnimationFrame
     * @param {Element|string} element - Element or selector
     * @param {Function} updateFn - Function to execute on next frame
     */
    smooth(element, updateFn) {
      const target = typeof element === 'string' ? Utility.DomSelector.$(element) : element;
      if (!target) return;
      
      requestAnimationFrame(() => updateFn(target));
    },

    /**
     * Set loading state on element
     * @param {Element|string} element - Element or selector
     * @param {boolean} isLoading - Loading state
     */
    setLoading(element, isLoading) {
      const target = typeof element === 'string' ? Utility.DomSelector.$(element) : element;
      if (!target) return;

      if (isLoading) {
        target.setAttribute('aria-disabled', true);
        target.classList.add('loading');
      } else {
        target.classList.remove('loading');
        target.removeAttribute('aria-disabled');
      }
    },

    /**
     * Vibrate mobile device for haptic feedback
     * @param {number|Array<number>} pattern - Vibration duration in ms or pattern (default: 100)
     * @example
     * Utility.Animate.vibrate(); // Single 100ms vibration
     * Utility.Animate.vibrate(200); // Single 200ms vibration
     * Utility.Animate.vibrate([100, 50, 100]); // Pattern: vibrate, pause, vibrate
     */
    vibrate(pattern = 100) {
      if (navigator.vibrate) {
        navigator.vibrate(pattern);
      }
    }
  },

  /**
   * Async form submit: native disabled, aria-busy, and theme spinner (`.btn.loading` + `.add-text` / `.spinner-border` in `buttons-badges.css`).
   * @param {HTMLButtonElement|HTMLElement|null|undefined} button
   * @param {boolean} isLoading
   */
  setSubmitButtonLoading(button, isLoading) {
    if (!button) return;
    const on = !!isLoading;
    if ('disabled' in button) {
      button.disabled = on;
    }
    if (on) {
      button.setAttribute('disabled', '');
      button.setAttribute('aria-busy', 'true');
    } else {
      button.removeAttribute('disabled');
      button.removeAttribute('aria-busy');
    }
    Utility.Animate.setLoading(button, on);
  },

   /**
   * Focus Management Utilities
   * ADA-compliant focus management for modals, drawers, and interactive elements
   */
  Focus: {
    _handlers: {},
    _activeContainer: null,
    _previouslyFocused: null,
    _forceTimeout: null,

    /**
     * Check if element is visible and focusable
     * @private
     */
    _isVisible(element) {
      const style = window.getComputedStyle(element);
      return style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0';
    },

    /**
     * Handle arrow key navigation between focusable elements
     * @param {KeyboardEvent} event - Keyboard event
     * @param {Element} container - Container to navigate within
     * @param {boolean} includeHidden - Include hidden elements
     * @returns {boolean} True if navigation was handled
     */
    handleArrowKeys(event, container, includeHidden = false) {
      if (!container) return false;
      
      const arrowKeys = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'];
      if (!arrowKeys.includes(event.key)) return false;
      if (!container.contains(document.activeElement)) return false;
      
      const focusableElements = this.getFocusableElements(container, includeHidden);
      if (!focusableElements.length) return false;
      
      event.preventDefault();
      
      const currentIndex = focusableElements.findIndex(el => el === document.activeElement || el.contains(document.activeElement));
      let nextIndex = currentIndex;
      
      if (currentIndex === -1) {
        // No element currently focused, start from first
        nextIndex = 0;
      } else if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
        nextIndex = currentIndex < focusableElements.length - 1 ? currentIndex + 1 : 0;
      } else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
        nextIndex = currentIndex > 0 ? currentIndex - 1 : focusableElements.length - 1;
      }
      
      if (nextIndex >= 0 && nextIndex < focusableElements.length) {
        focusableElements[nextIndex].focus();
        return true;
      }
      
      return false;
    },

    /**
     * Get all focusable elements within a container
     * @param {Element} container
     * @param {boolean} includeHidden - Include hidden elements
     * @returns {Array<Element>}
     */
    getFocusableElements(container, includeHidden = false) {
      if (!container) {
        console.warn('Focus.getFocusableElements: No container provided');
        return [];
      }

      const selector = [
        // Custom theme elements
        '.filter__container',
        // Links
        'a[href]',
        'a[data-href]',
        // Buttons and inputs
        '[type="button"]',
        'button:enabled',
        'input:not([type=hidden]):not([tabindex^="-"]):enabled',
        'select:enabled',
        'textarea:enabled',
        // Interactive elements
        'summary',
        '[contenteditable]:not([contenteditable="false"])',
        'details',
        // Media with controls
        'audio[controls]',
        'video[controls]',
        // Embedded content
        'embed',
        'object',
        'iframe',
        // Map area
        'area[href]',
        // Custom focusable
        '[tabindex]:not([tabindex^="-"])',
        '[draggable="true"]'
      ].join(', ');

      const elements = Array.from(container.querySelectorAll(selector));

      return elements.filter(el => {
        if (el.getAttribute('aria-hidden') === 'true' || el.hasAttribute('inert')) return false;
        return includeHidden || this._isVisible(el);
      });
    },

    /**
     * Force focus on an element
     * @param {Element} element
     * @param {object} options - {preventScroll, timeout}
     * @returns {boolean} Success status
     */
    force(element, options = {}) {
      const { preventScroll = false, timeout = 500 } = options;

      if (!element || !this._isVisible(element)) {
        console.warn('Focus.force: Invalid or invisible element', element);
        return false;
      }

      if (this._forceTimeout) clearTimeout(this._forceTimeout);

      element.focus({ preventScroll });
      if (element === document.activeElement) return true;

      // Retry with tabindex
      const hadTabindex = element.hasAttribute('tabindex');
      const originalTabindex = element.getAttribute('tabindex');
      element.setAttribute('tabindex', '0');
      
      this._forceTimeout = setTimeout(() => {
        element.focus({ preventScroll });
        if (!hadTabindex) {
          element.removeAttribute('tabindex');
        } else if (originalTabindex !== '0') {
          element.setAttribute('tabindex', originalTabindex);
        }
        if (element !== document.activeElement) {
          console.warn('Focus.force: Unable to focus after retry');
        }
      }, timeout);

      return element === document.activeElement;
    },

    trap(container, elementToFocus = null, options = {}) {
      const { returnFocus = true, skipVisibilityCheck = false, includeHidden = false, enableArrowKeys = false } = options;

      if (!container) return false;
      if (!skipVisibilityCheck && !this._isVisible(container)) {
        requestAnimationFrame(() => setTimeout(() => this.trap(container, elementToFocus, { ...options, skipVisibilityCheck: true, includeHidden: true }), 50));
        return false;
      }

      const checkHidden = includeHidden || skipVisibilityCheck;
      const elements = this.getFocusableElements(container, checkHidden);
      if (!elements.length) { container.setAttribute('tabindex', '-1'); return false; }

      if (returnFocus && document.activeElement !== document.body) this._previouslyFocused = document.activeElement;
      this.release(null, false);
      this._activeContainer = container;

      this._handlers.keydown = (e) => {
        // Tab key navigation (existing behavior)
        if (e.key === 'Tab' || e.keyCode === 9) {
          const current = this.getFocusableElements(container, checkHidden);
          if (!current.length || !container.contains(document.activeElement)) return;
          const [first, last] = [current[0], current[current.length - 1]];
          if ((document.activeElement === last && !e.shiftKey) || (document.activeElement === first && e.shiftKey)) {
            e.preventDefault();
            (document.activeElement === last ? first : last).focus();
          }
          return;
        }

        // Arrow key navigation (optional, for menus/dropdowns)
        if (enableArrowKeys) {
          this.handleArrowKeys(e, container, checkHidden);
        }
      };

      this._handlers.focusin = (e) => {
        if (this._activeContainer && !this._activeContainer.contains(e.target)) {
          e.preventDefault(); e.stopPropagation();
          this.getFocusableElements(container, checkHidden)[0]?.focus();
        }
      };

      document.addEventListener('keydown', this._handlers.keydown, true);
      document.addEventListener('focusin', this._handlers.focusin, true);
      requestAnimationFrame(() => this.force(elementToFocus || elements[0]));
      return true;
    },

    /**
     * Release focus trap
     * @param {Element} elementToFocus - Element to focus after release
     * @param {boolean} usePrevious - Use stored previous focus
     */
    release(elementToFocus = null, usePrevious = true) {
      // Remove event listeners with capture flag
      ['focusin', 'keydown'].forEach(type => {
        if (this._handlers[type]) {
          document.removeEventListener(type, this._handlers[type], true);
        }
      });

      this._handlers = {};
      this._activeContainer = null;

      if (this._forceTimeout) {
        clearTimeout(this._forceTimeout);
        this._forceTimeout = null;
      }

      const returnElement = elementToFocus || (usePrevious ? this._previouslyFocused : null);
      
      if (returnElement?.isConnected) {
        try {
          returnElement.focus();
        } catch (error) {
          console.warn('Focus.release: Unable to return focus', error);
        }
      }

      this._previouslyFocused = null;
    },

    /** Check if focus is trapped */
    isTrapped() {
      return this._activeContainer !== null;
    },

    /** Get active trapped container */
    getActiveContainer() {
      return this._activeContainer;
    },

    /**
     * Store element identifier for focus restoration after DOM update
     * @param {Element} element - Element to store (defaults to activeElement)
     * @param {Element} container - Optional container to check if element is within
     * @returns {Object|null} Stored element identifier or null
     */
    storeForRestore(element = null, container = null) {
      const activeElement = element || document.activeElement;
      if (!activeElement) return null;
      
      // Check if element is within container if provided
      if (container && !container.contains(activeElement)) return null;
      
      // Store element identifier - prefer ID, fallback to selector
      if (activeElement.id) {
        return { selector: `#${activeElement.id}` };
      }
      if (activeElement.getAttribute('for')) {
        return { selector: `label[for="${activeElement.getAttribute('for')}"]` };
      }
      if (activeElement.href) {
        return { selector: `a[href="${activeElement.href}"]` };
      }
      // Fallback to element reference if still connected
      return activeElement.isConnected ? { element: activeElement } : null;
    },

    /**
     * Restore focus to a previously stored element
     * @param {Object} storedElement - Element identifier from storeForRestore
     * @param {Element} context - Optional context element to search within
     * @returns {boolean} True if focus was restored
     */
    restore(storedElement, context = document) {
      if (!storedElement) return false;
      
      let elementToFocus = null;
      
      if (storedElement.selector) {
        elementToFocus = Utility.DomSelector.$(storedElement.selector, context);
      } else if (storedElement?.element?.isConnected) {
        elementToFocus = storedElement.element;
      }
      
      if (elementToFocus?.isConnected) {
        requestAnimationFrame(() => {
          elementToFocus.focus();
        });
        return true;
      }
      
      return false;
    }
  },

  /**
   * Fetch utilities with error handling and common patterns
   */
   Fetch: {
    /**
     * Fetch with automatic error handling
     * @param {string} url - Request URL
     * @param {object} options - Fetch options
     * @returns {Promise<Response>}
     */
    async request(url, options = {}) {
      try {
        const response = await fetch(url, options);
        if (!response.ok) {
          const error = new Error(`HTTP error! status: ${response.status}`);
          error.status = response.status;
          error.response = response;

          try {
            const contentType = response.headers.get('content-type') || '';
            const isJson = contentType.includes('application/json');
            const data = isJson ? await response.json() : await response.text();
            error.data = data;

            if (isJson) {
              const message = data?.message?.trim() || data?.description?.trim();
              if (message) error.message = message;
            } else if (data?.trim()) {
              error.message = data.trim();
            }
          } catch (parseError) {
            error.parseError = parseError;
          }

          throw error;
        }
        return response;
      } catch (error) {
        console.error('Fetch error:', error);
        const fallback = error?.message || 'Request Failed';
        Utility.handleError(fallback, 'Request Failed');
        throw error;
      }
    },

    /**
     * Fetch JSON data
     * @param {string} url
     * @param {object} options
     * @returns {Promise<object>}
     */
    async json(url, options = {}) {
      const response = await this.request(url, options);
      return response.json();
    },

    /**
     * Fetch HTML text
     * @param {string} url
     * @param {object} options
     * @returns {Promise<string>}
     */
    async html(url, options = {}) {
      const response = await this.request(url, options);
      return response.text();
    },

    /**
     * Fetch Shopify section HTML
     * @param {string} url - Base URL (can include existing query parameters)
     * @param {string|array} sectionIds - Section ID(s)
     * @returns {Promise<object>}
     */
    async section(url, sectionIds) {
      const sections = Array.isArray(sectionIds) ? sectionIds.join(',') : sectionIds;
      const separator = url.includes('?') ? '&' : '?';
      const sectionUrl = `${url}${separator}sections=${sections}`;
      return await this.json(sectionUrl);
    },

    /**
     * Fetch product data by handle
     * @param {string} handle - Product handle
     * @param {string} view - View template (optional)
     * @returns {Promise<string>}
     */
    async product(handle, view = null) {
      const url = view ? `/products/${handle}?view=${view}` : `/products/${handle}`;
      return this.html(url);
    },

    /**
     * Build fetch config for POST requests
     * @param {string} type - request Accept type ('json' or 'javascript')
     * @param {object} config - Additional config (headers, body)
     * @returns {object} Fetch configuration object
     */
    config(type = 'json', config = {}) {
      /** @type {Headers} */
      const headers = { 'Content-Type': 'application/json', Accept: `application/${type}`, ...config.headers };

      if (type === 'javascript') {
        headers['X-Requested-With'] = 'XMLHttpRequest';
        delete headers['Content-Type'];
      }

      return {
        method: 'POST',
        headers: /** @type {HeadersInit} */ (headers),
        body: config.body,
      };
    }
  },

  /**
   * Storage Management Utilities
   * Unified methods for cookies, localStorage, and sessionStorage
   */
  Storage: {
    Cookie: {
      set(name, value, options = {}) {
        const { days = 365, path = '/', domain = '', secure = false, sameSite = 'Lax' } = options;
        const val = typeof value === 'object' ? JSON.stringify(value) : String(value);
        const date = new Date(Date.now() + days * 864e5);
        let cookie = `${encodeURIComponent(name)}=${encodeURIComponent(val)}; expires=${date.toUTCString()}; path=${path}`;
        if (domain) cookie += `; domain=${domain}`;
        if (secure) cookie += '; secure';
        if (sameSite) cookie += `; samesite=${sameSite}`;
        document.cookie = cookie;
        return true;
      },

      get(name, parseJSON = false) {
        const nameEQ = `${encodeURIComponent(name)}=`;
        const cookie = document.cookie.split(';').find(c => c.trim().indexOf(nameEQ) === 0);
        if (!cookie) return null;
        const value = decodeURIComponent(cookie.trim().substring(nameEQ.length));
        return parseJSON ? ((() => { try { return JSON.parse(value); } catch { return value; } })()) : value;
      },

      remove(name, options = {}) {
        return this.set(name, '', { ...options, days: -1 });
      },

      exists(name) {
        return this.get(name) !== null;
      },

      getAll() {
        const cookies = {};
        document.cookie.split(';').forEach(c => {
          const [n, v] = c.trim().split('=');
          if (n) cookies[decodeURIComponent(n)] = decodeURIComponent(v || '');
        });
        return cookies;
      }
    },

    Local: {
      set(key, value, options = {}) {
        try {
          const data = { value, timestamp: Date.now(), ...(options.ttl && { expiry: Date.now() + options.ttl }) };
          localStorage.setItem(key, JSON.stringify(data));
          return true;
        } catch { return false; }
      },

      get(key, defaultValue = null) {
        try {
          const item = localStorage.getItem(key);
          if (!item) return defaultValue;
          const data = JSON.parse(item);
          if (data.expiry && Date.now() > data.expiry) {
            this.remove(key);
            return defaultValue;
          }
          return data.value;
        } catch {
          return localStorage.getItem(key) || defaultValue;
        }
      },

      remove(key) { try { localStorage.removeItem(key); return true; } catch { return false; } },
      clear() { try { localStorage.clear(); return true; } catch { return false; } },
      exists(key) { return localStorage.getItem(key) !== null; },
      keys() { return Object.keys(localStorage); },
      size() { return Object.keys(localStorage).reduce((s, k) => s + k.length + localStorage[k].length, 0); }
    },

    Session: {
      set(key, value) {
        try {
          sessionStorage.setItem(key, JSON.stringify({ value, timestamp: Date.now() }));
          return true;
        } catch { return false; }
      },

      get(key, defaultValue = null) {
        try {
          const item = sessionStorage.getItem(key);
          return item ? JSON.parse(item).value : defaultValue;
        } catch {
          return sessionStorage.getItem(key) || defaultValue;
        }
      },

      remove(key) { try { sessionStorage.removeItem(key); return true; } catch { return false; } },
      clear() { try { sessionStorage.clear(); return true; } catch { return false; } },
      exists(key) { return sessionStorage.getItem(key) !== null; },
      keys() { return Object.keys(sessionStorage); }
    }
  },

  /**
   * DOM Utilities
   * Methods for parsing and manipulating HTML
   */
  DOM: {
    /**
     * Parse HTML string to DOM
     * @param {string} htmlString - HTML string to parse
     * @returns {Document}
     */
    parse(htmlString) {
      return new DOMParser().parseFromString(htmlString, 'text/html');
    },

    /**
     * Update destination element from source HTML
     * @param {string} id - Selector for element to update
     * @param {Document} html - Parsed HTML document
     * @param {Element} container - Target container (default: document)
     */
    update(id, html, container = document) {
      const source = html.querySelector(id);
      const destination = container.querySelector(id);

      if (source && destination) {
        destination.innerHTML = source.innerHTML;
        destination.classList.remove('d-none');
      } else {
        destination?.classList.add('d-none');
      }
    },

    /**
     * Update multiple different selectors from parsed HTML
     * Updates the first match of each selector in the array
     * @param {Array<string>} selectors - Array of selectors to update
     * @param {Document} sourceHTML - Parsed HTML document
     * @param {Element} container - Target container (default: document)
     */
    updateSelectors(selectors, sourceHTML, container = document) {
      selectors.forEach(selector => this.update(selector, sourceHTML, container));
    },

    /**
     * Keeps Points/Bonus line-property holders in sync after variant section HTML swap.
     * Copies hidden input values and data-points / data-bonus attributes (guest flow).
     * @param {ParentNode} container
     * @param {ParentNode} html
     */
    syncConsultantVariantLineProperties(container = document, html = null) {
      if (!container?.querySelector || !html?.querySelector) return;

      [
        { selector: '[data-variant-consultant-points]', dataKey: 'points' },
        { selector: '[data-variant-consultant-bonus]', dataKey: 'bonus' }
      ].forEach(({ selector, dataKey }) => {
        const source = html.querySelector(selector);
        const destination = container.querySelector(selector);
        if (!source || !destination) return;

        if (selector === '[data-variant-consultant-bonus]') {
          ['bonusUs', 'bonusCa', 'bonus'].forEach((key) => {
            if (source.dataset[key]) {
              destination.dataset[key] = source.dataset[key];
            } else {
              delete destination.dataset[key];
            }
          });
          Utility.applyConsultantBonusForCountry(container);
          return;
        }

        const pointsName = Utility.getConsultantPointsPropertyName();
        const sourceInput =
          source.querySelector(`input[name="${pointsName}"]`) ||
          source.querySelector('input[name="properties[Points]"]') ||
          source.querySelector('input[name="properties[_Points]"]') ||
          source.querySelector('input[type="hidden"]');
        const destinationInput =
          destination.querySelector(`input[name="${pointsName}"]`) ||
          destination.querySelector('input[name="properties[Points]"]') ||
          destination.querySelector('input[name="properties[_Points]"]') ||
          destination.querySelector('input[type="hidden"]');
        if (sourceInput && destinationInput) {
          destinationInput.setAttribute('name', pointsName);
          destinationInput.value = sourceInput.value;
        }

        const value = String(
          sourceInput?.value ||
            source.dataset[dataKey] ||
            ''
        ).trim();

        if (value) {
          destination.dataset[dataKey] = value;
        } else {
          delete destination.dataset[dataKey];
        }
      });
    },

    /**
     * Render HTML stored in variant product_details text metafields (not rich_text_field).
     * @param {ParentNode} [container]
     */
    hydrateVariantProductDetailsHtml(container = document) {
      const scope = container === document ? document : container;
      if (!scope?.querySelectorAll) return;

      scope.querySelectorAll('[data-variant-product-details]').forEach((root) => {
        const source = root.querySelector('script.variant-product-details-source');
        if (source) {
          try {
            const html = JSON.parse(source.textContent);
            if (typeof html === 'string' && html.trim()) {
              root.innerHTML = html;
              return;
            }
          } catch {
            /* fall through */
          }
        }

        const field = root.querySelector(
          '.metafield-multi_line_text_field, .metafield-single_line_text_field'
        );
        if (!field) return;

        const text = (field.textContent || '').trim();
        if (/^<[a-z][\s\S]*>/i.test(text)) {
          root.innerHTML = text;
        }
      });
    },

    /**
     * Hide product page collapsible rows when toggle-content-wrap has no meaningful content.
     * @param {ParentNode} [container]
     * @param {ParentNode} [html] - Optional parsed section HTML from variant change
     */
    syncProductCollapsibleRows(container = document, html = null) {
      const scope = container === document ? document : container;
      if (!scope?.querySelectorAll) return;

      scope.querySelectorAll('[data-product-collapsible-row]').forEach((destPanel) => {
        const blockId = destPanel.dataset.collapsibleRowId;
        const sourcePanel =
          html && blockId
            ? html.querySelector(`[data-collapsible-row-id="${CSS.escape(blockId)}"]`)
            : null;
        const destWrap = destPanel.querySelector('.toggle-content-wrap');

        let isEmpty = true;
        if (destWrap) {
          const hasMedia = Boolean(
            destWrap.querySelector('img, video, iframe, svg, picture, audio')
          );
          const text = (destWrap.textContent || '').replace(/\s+/g, ' ').trim();
          isEmpty = !hasMedia && text.length === 0;
        }

        if (sourcePanel) {
          isEmpty = sourcePanel.classList.contains('d-none') || isEmpty;
        }

        destPanel.classList.toggle('d-none', isEmpty);
      });
    },

    /**
     * Update all matching elements from parsed HTML (handles NodeList)
     * Updates all elements that match the selector, not just the first one.
     * If there are more destinations than sources, extra destinations will use
     * the first source as a fallback template.
     * @param {string} selector - CSS selector (may match multiple elements)
     * @param {Document} html - Parsed HTML document
     * @param {Element} container - Target container (default: document)
     */
    updateAll(selector, html, container = document) {
      const sources = html.querySelectorAll(selector);
      const destinations = container.querySelectorAll(selector);

      if (destinations.length === 0) {
        return;
      }else if(sources.length === 0) {
        destinations.forEach(dest => dest.classList.add('d-none'));
        return;
      }

      destinations.forEach((destination, index) => {
        const source = sources[index] || sources[0];
        destination.innerHTML = source.innerHTML;
        destination.classList.remove('d-none');
      });
    },

    /**
     * Extract element from HTML string
     * @param {string|Document} htmlString - HTML string or parsed document
     * @param {string} selector - CSS selector
     * @returns {Element|null}
     */
    extract(htmlString, selector) {
      const parsed = typeof htmlString === 'string' ? this.parse(htmlString) : htmlString;
      return parsed.querySelector(selector);
    }
  },

  /**
   * Form Utilities
   * Methods for form handling and validation
   */
  Form: {
    /**
     * Serialize form data
     * @param {HTMLFormElement} form - Target form element
     * @returns {Object} Serialized form data
     */
    serialize(form) {
      if (!(form instanceof HTMLFormElement)) {
        throw new TypeError('Expected a form element');
      }

      const obj = {};
      const formData = new FormData(form);
      const regex = /^properties\[(.*?)\]$/;

      for (const key of formData.keys()) {
        const match = regex.exec(key);
        if (match) {
          obj.properties = obj.properties || {};
          obj.properties[match[1]] = formData.get(key);
        } else {
          obj[key] = formData.get(key);
        }
      }
      return obj;
    },

    /**
     * Validate email format
     * @param {string} email - Email to validate
     * @returns {boolean} Whether email is valid
     */
    validateEmail(email) {
      return !!email.match(/^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/);
    },

    /**
     * Validate form and set ARIA attributes
     * @param {HTMLFormElement} form - Form to validate
     * @returns {Array<string>} Array of error messages
     */
    validate(form) {
      const errors = [];
      const requiredFields = form.querySelectorAll('[required]');

      requiredFields.forEach(field => {
        if (!field.value.trim()) {
          field.setAttribute('aria-invalid', 'true');
          field.setAttribute('aria-describedby', `error-${field.id}`);
          errors.push(`${field.getAttribute('aria-label') || field.name} is required`);
        } else {
          field.removeAttribute('aria-invalid');
        }
      });

      return errors;
    }
  },

   /**
   * Cart Management Utilities
   */
  Cart: {
    /** Chain of every runExclusive() task queued so far — see runExclusive(). */
    _mutationQueue: Promise.resolve(),

    /**
     * Runs `fn` only after every previously-queued cart-mutating task has
     * settled, so at most one write to the cart's line-item properties /
     * attributes is ever in flight at a time across the whole page.
     * Shopify's Cart AJAX API isn't safe for concurrent writes — two
     * requests each read-modify-write the same server-side cart JSON, and
     * whichever POST/PUT lands last effectively wins, silently discarding
     * whatever the other one changed. On this theme that meant, e.g., the
     * consultant Points/Bonus backfill's own /cart/change.js patches (see
     * component-cart-form.js) racing against check-consultant.js's
     * WatkinsConsultantCart.handleCartStateAfterChange() /cart/update.js
     * call — both fire off the same `cartUpdate` event after an add-to-cart
     * — and each write could clobber a property the other had just set,
     * which is what produced the Points/Bonus line flip-flopping between
     * "just added" and "missing again" instead of settling once.
     *
     * Every cart-mutating call site (line-property backfill, cart attribute
     * sync, etc.) must route its request through this instead of calling
     * fetch() directly, or it isn't actually protected by the queue.
     * @template T
     * @param {() => Promise<T>} fn
     * @returns {Promise<T>}
     */
    runExclusive(fn) {
      const run = () => Promise.resolve().then(fn);
      const result = Utility.Cart._mutationQueue.then(run, run);
      // Swallow the error here so one failed task doesn't permanently wedge
      // the queue for every task queued after it — the real rejection is
      // still delivered to whoever awaited `result` below.
      Utility.Cart._mutationQueue = result.catch(() => {});
      return result;
    },

    /**
     * Update cart count in header
     */
    updateHeaderCount() {
      const cartIcons  = document.querySelectorAll('[data-cart-icon]');
      cartIcons.forEach(icon => {
        const cartCount = icon.querySelector('[data-cart-count]');
        if(cartCount) {
          cartCount.innerHTML = globalThis.themeData.cart.item_count;
          cartCount.classList.toggle('d-none', globalThis.themeData.cart.item_count === 0);
        }
      });
    },

    /**
     * Add item to cart
     * @param {object} formData - Form data or item object
     * @returns {Promise<object>}
     */
    async addItem(formData) {
      if (Utility.requireCustomerLoginForCart()) {
        return Promise.reject(new Error('Login required'));
      }

      const config = Utility.Fetch.config('json', {
        body: JSON.stringify(formData)
      });
      
      const response = await fetch(globalThis.routes.cart_add_url, config);
      const data = await response.json();
      
      if (data.status === 422 || data.description) {
        throw new Error(data.description || 'Unable to add to cart');
      }

      return data;
    },

    /** sessionStorage key for the most recent add-to-cart, checked on the next page load. */
    _RECENT_ADDITION_STORAGE_KEY: 'watkins_recent_cart_addition',

    /**
     * Remembers item(s) just added to cart so restoreMissingCartAdditions()
     * (called early on the next page load) can verify they actually stuck
     * and re-add them if some background process silently emptied the cart
     * first — e.g. the automatic US/CA market-switch reload or the
     * different-customer cart-clear both do a full page reload shortly
     * after an add can land, and either can otherwise discard it. This is a
     * general safety net, not tied to either specific mechanism: it just
     * guarantees whatever the shopper deliberately added is still there one
     * page load later, regardless of what tried to clear it.
     *
     * One-shot: restoreMissingCartAdditions() clears this the moment it
     * checks, so it never resurrects something the shopper removed
     * themselves on a later page.
     * @param {Array<{id?: number, variant_id?: number, quantity: number, properties?: Record<string,string>}> | {id?: number, variant_id?: number, quantity: number, properties?: Record<string,string>}} items
     */
    rememberAddedItems(items) {
      try {
        const payload = (Array.isArray(items) ? items : [items])
          .map((item) => ({
            id: Number(item.variant_id ?? item.id),
            quantity: Number(item.quantity) || 1,
            ...(item.properties && Object.keys(item.properties).length ? { properties: item.properties } : {})
          }))
          .filter((item) => item.id > 0 && item.quantity > 0);
        if (!payload.length) return;
        globalThis.sessionStorage.setItem(Utility.Cart._RECENT_ADDITION_STORAGE_KEY, JSON.stringify(payload));
      } catch {
        /* sessionStorage unavailable (private mode, quota, etc.) */
      }
    },

    /**
     * Verifies whatever rememberAddedItems() stashed is still actually in
     * the cart, and re-adds anything missing (matched by variant id + at
     * least the remembered quantity — properties aren't compared, since a
     * legitimate downstream process, e.g. the consultant Points/Bonus
     * backfill, may have already patched them). Call once, early, on every
     * page load.
     */
    async restoreMissingCartAdditions() {
      let raw = null;
      try {
        raw = globalThis.sessionStorage.getItem(Utility.Cart._RECENT_ADDITION_STORAGE_KEY);
        if (raw) globalThis.sessionStorage.removeItem(Utility.Cart._RECENT_ADDITION_STORAGE_KEY);
      } catch {
        return;
      }
      if (!raw) return;

      let expected;
      try {
        expected = JSON.parse(raw);
      } catch {
        return;
      }
      if (!Array.isArray(expected) || !expected.length) return;

      try {
        const cartResponse = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        if (!cartResponse.ok) return;
        const cart = await cartResponse.json();

        const currentQtyByVariant = new Map();
        (cart.items || []).forEach((item) => {
          const key = Number(item.variant_id);
          currentQtyByVariant.set(key, (currentQtyByVariant.get(key) || 0) + Number(item.quantity));
        });

        const missing = expected.filter((item) => (currentQtyByVariant.get(item.id) || 0) < item.quantity);
        if (!missing.length) return;

        const addResponse = await fetch(globalThis.routes?.cart_add_url || '/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: missing })
        });
        if (!addResponse.ok) return;

        globalThis.location.reload();
      } catch {
        /* best-effort restore; nothing more to do if this also fails */
      }
    }
  },

  /**
    * Limit the number of times a function is called within defined timeout
    *
    * @param {function} fn - function to execute
    * @param {integer} wait - Timeout to fire event
    * @returns {Function} Debounced function with optional cancel() method
  */
  debounce(fn, wait) {
    let t;
    const debounced = (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), wait);
    };
    debounced.cancel = () => {
      clearTimeout(t);
      t = null;
    };
    return debounced;
  },

  /**
   * Format date
   * @param  {string}
   * @return {String} value - formatted value
   */
  formatDate(value) {
    const date = new Date(value);
    const ye = new Intl.DateTimeFormat('en', { year: 'numeric' }).format(date);
    const mo = new Intl.DateTimeFormat('en', { month: 'short' }).format(date);
    const da = new Intl.DateTimeFormat('en', { day: '2-digit' }).format(date);
    return `${mo} ${da} ${ye}`;
  },

  /**
   * Get swatch hex color code or image url
   *
   * @param {string} colorName
   * @returns {string} background style hex code
  */
  getSwatchStyle(colorName) {
    if (!this._swatchesCache) {
      const swatchesElement = Utility.DomSelector.$('[data-swatches-colorlist-json]');
      if (swatchesElement) {
        try {
          this._swatchesCache = JSON.parse(swatchesElement.innerText);
        } catch (e) {
          console.warn('Failed to parse swatches color list', e);
          this._swatchesCache = {}; // Prevent repeated errors
        }
      } else {
        this._swatchesCache = {}; // Element not found, prevent repeated queries
      }
    }
    
    if (!this._swatchesCache || Object.keys(this._swatchesCache).length === 0) {
      return false;
    }

    colorName = colorName.replace(/-|\s/g, '').toLowerCase();
    const swatch = this._swatchesCache[colorName];
    let swatchStyle;
    if (typeof swatch !== 'undefined') {
      if (swatch.match(/\.(jpeg|jpg|png|gif)/g) != null) {
        swatchStyle = `background-image: url(${swatch}); background-size: cover; background-position: center;`;
      } else {
        swatchStyle = `background-color: ${swatch}`;
      }
      return swatchStyle;
    }
    return false;
  },

  /**
  * Toggle element visibility
  * @param {Element} container - Container element
  * @param {string} activity - Activity to toggle
  * @returns {void}
   */
  toggleElement(container, activity) {
    const contentBlock = container.querySelector('[data-type="content"]') || container.querySelector('.toggle-content');
    if(!contentBlock) return;

    if(activity == 'open'){
      Utility.CSS.add([contentBlock, container], 'open');
      contentBlock.removeAttribute('inert');
      contentBlock.setAttribute('aria-hidden', 'false');
    }else{
      Utility.CSS.remove([contentBlock, container], 'open');
      contentBlock.setAttribute('inert', '');
      contentBlock.setAttribute('aria-hidden', 'true');
    }
  },

  /**
   * Live Region Manager - Persistent ARIA live regions for screen reader announcements
   * @private
   */
  _liveRegions: {},

  /**
   * Get or create live region
   * @private
   */
  _getLiveRegion(priority) {
    if (!this._liveRegions[priority]) {
      const region = document.createElement('div');
      region.setAttribute('aria-live', priority);
      region.setAttribute('aria-atomic', 'true');
      region.className = 'visually-hidden';
      document.body.appendChild(region);
      this._liveRegions[priority] = region;
    }
    return this._liveRegions[priority];
  },

  /**
   * Announce message to screen readers
   * @param {string} message - Message to announce
   * @param {string} priority - 'polite' or 'assertive' (default: 'polite')
   */
  announceToScreenReader(message, priority = 'polite') {
    const region = this._getLiveRegion(priority);
    
    // Clear previous message
    region.textContent = '';
    
    // Set new message after a brief delay (ensures screen readers detect change)
    setTimeout(() => {
      region.textContent = message;
    }, 100);
  },

  /**
   * Format money values based on your shop currency settings
   * @param  {Number|string} cents - value in cents or dollar amount
   * @return {String} value - formatted value
   */
  formatMoney(cents, format) {
    format = format || globalThis.themeData.money_format || "${{ amount }}";
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const numericCents = Number(cents);

    const formatWithDelimiters = (num, prec = 2, thou = ',', dec = '.') => {
      if (!Number.isFinite(num)) {
        const zero = (0).toFixed(prec);
        const zparts = zero.split('.');
        return zparts[0] + (zparts[1] ? dec + zparts[1] : '');
      }
      const fixed = (num / 100).toFixed(prec);
      const parts = fixed.split('.');
      return parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thou}`) + (parts[1] ? dec + parts[1] : '');
    };

    const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!match) {
      return formatWithDelimiters(numericCents, 2);
    }

    const type = match[1];
    const value = {
      amount: formatWithDelimiters(numericCents, 2),
      amount_no_decimals: formatWithDelimiters(numericCents, 0),
      amount_with_comma_separator: formatWithDelimiters(numericCents, 2, '.', ','),
      amount_no_decimals_with_comma_separator: formatWithDelimiters(numericCents, 0, '.', ',')
    }[type];

    const resolved = value !== undefined ? value : formatWithDelimiters(numericCents, 2);
    return format.replace(/\{\{\s*\w+\s*\}\}/, resolved);
  },

  /**
   * Formatted money as plain text (shop money_format often wraps HTML; use for buttons / aria).
   * @param {Number|string} cents
   * @param {string} [format]
   * @returns {string}
   */
  formatMoneyText(cents, format) {
    const raw = Utility.formatMoney(cents, format);
    if (typeof document === 'undefined' || !raw) return raw;
    const el = document.createElement('div');
    el.innerHTML = raw;
    const text = el.textContent?.trim();
    return text || raw;
  },

  /**
   * Extract error message from various error formats
   * Handles Shopify API errors, fetch errors, and generic errors
   * @param {Error|Object|string} error - Error object or message
   * @returns {string} Extracted error message
   */
  extractErrorMessage(error) {
    // Handle string errors
    if (typeof error === 'string') {
      return error.trim() || 'An error occurred';
    }

    // Handle error objects with data property (Shopify API format)
    if (error?.data) {
      const data = typeof error.data === 'string' 
        ? (() => {
            try {
              return JSON.parse(error.data);
            } catch {
              return { message: error.data };
            }
          })()
        : error.data;

      return data?.message || data?.description || data?.error || data?.errors || 'An error occurred';
    }

    // Handle error objects with message property
    if (error?.message) {
      return error.message;
    }

    // Fallback
    return 'An error occurred';
  },

  handleError(e, title = 'Error') {
    const message = this.extractErrorMessage(e);
    window.notificationEle.updateNotification(title, message, {
      type: 'error',
      timeout: 15000
    });
  },

  handleSuccess(e, title = 'Success') {
    window.notificationEle.updateNotification(title, e, {
      type: 'success',
      timeout: 15000
    });
  }
}